import { Trans } from '@lingui/macro';
import React, { useEffect, useState, FunctionComponent } from 'react';

import { FetchState, useFetch, useNotifyFetchError } from '@api/ApiHelper';
import { IndexDetailedUsersResponse, UserRoles } from '@api/v4/private/resources/users';
import { PutUserPermissionResponse } from '@api/v4/resources/user-permissions';
import { usePermissionOptions } from '@components/bulk_management/user-settings/helpers';
import { ListDropdown, ListOption } from '@components/library/dropdown';

interface UserPermissionProps {
  isSelf: boolean;
  permissionLevel: UserRoles;
  resourceType: string;
  setReloadPlanLimits: SetStateDispatch<boolean>;
  userId: string;
  userPermissionKey: string;
  usersFetch: FetchState<IndexDetailedUsersResponse>;
}

export const UserPermission: FunctionComponent<UserPermissionProps> = ({
  isSelf,
  permissionLevel,
  resourceType,
  setReloadPlanLimits,
  userId,
  userPermissionKey,
  usersFetch
}) => {
  const [newPermissionLevel, setNewPermissionLevel] = useState<UserRoles | undefined>();
  const permissionOptions = usePermissionOptions(resourceType);
  const updateUserPermissionFetch = useFetch<PutUserPermissionResponse>({
    url: `/api/v4/user_permissions/${userPermissionKey}`,
    method: 'PUT',
    body: {
      data: {
        attributes: {
          permission_level: newPermissionLevel // eslint-disable-line @typescript-eslint/naming-convention
        }
      }
    },
    fetchOnMount: false
  });

  useNotifyFetchError(updateUserPermissionFetch);

  useEffect(() => {
    if (newPermissionLevel) {
      updateUserPermissionFetch.fetch();
    }
  }, [newPermissionLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateUserPermissionFetch.response) {
      usersFetch.fetch();
      setReloadPlanLimits(true);
    }
  }, [updateUserPermissionFetch.response]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateUserPermissionFetch.error) {
      setNewPermissionLevel(undefined);
    }
  }, [updateUserPermissionFetch.error]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isSelf) {
    // admins cannot update their own permissions
    return (
      <span className="manage-users-table__static-permission-level">
        {permissionOptions.find((opt) => opt.value === permissionLevel)?.label || <Trans>None</Trans>}
      </span>
    );
  }

  // Map permission values to readable labels for tracking
  const getPermissionLabel = (value: string | UserRoles): string => {
    const permissionLabelMap: Record<string, string> = {
      [UserRoles.Guest]: 'Guest',
      [UserRoles.Collaborator]: 'Collaborator',
      [UserRoles.Admin]: 'Admin',
      [UserRoles.Owner]: 'Owner'
    };

    // Check if it's a custom role (not in the standard map)
    if (!permissionLabelMap[value]) {
      // Try to find it in permissionOptions (for custom roles)
      const matchingOption = permissionOptions.find(opt => opt.value === value);
      if (matchingOption) {
        if (typeof matchingOption.label === 'string') {
          return matchingOption.label;
        }
        // For custom roles, the label might be a string directly
        if (matchingOption.label && typeof matchingOption.label === 'object' && 'label' in matchingOption.label) {
          return String((matchingOption.label as { label: string }).label);
        }
      }
      return String(value);
    }

    return permissionLabelMap[value] || String(value);
  };

  return (
    <ListDropdown
      onChange={(selectedOption: ListOption): void => {
        // Track permission change with Amplitude
        if (window.BFG?.amplitude?.enabled && window.amplitudeService) {
          const permissionLabel = getPermissionLabel(selectedOption.value as UserRoles);

          window.amplitudeService.trackEvent('MenuSelect', {
            elementName: 'ChangeUserPermission',
            elementLocation: 'UserManagementPage',
            productArea: 'BulkManagementPage',
            featureArea: 'UserManagementPage',
            eventName: 'MenuSelect',
            'key properties': {
              userId: userId,
              newPermission: permissionLabel
            }
          });
        }

        setNewPermissionLevel(selectedOption.value as UserRoles);
      }}
      options={permissionOptions}
      value={newPermissionLevel || permissionLevel}
      virtualizeOptions={false}
    />
  );
};
